/**
 * L0 创作转化漏斗看板（零埋点 / Design §5.3）
 *
 * 作品→讲述→完成对话→定稿→购买→打印 漏斗 + 讲述均时 + PDF/分享。
 * 数据全部由 content-pipeline 现有业务表聚合，无需埋点。
 */
import { useState } from 'react';
import { Card, Row, Col, Statistic, Space, Result, Alert } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { ChartContainer } from '../../components/dashboard/ChartContainer';
import { TimeSlicer, type TimeSlicerValue } from '../../components/dashboard/TimeSlicer';
import { PERMISSIONS } from '../../contracts/permissions';
import { getCreationFunnel } from '../../api/dashboard';

const DEFAULT_RANGE_DAYS = 180;
function defaultRange(): { startAt: string; endAt: string } {
  const end = new Date();
  const start = new Date(end.getTime() - DEFAULT_RANGE_DAYS * 24 * 3600 * 1000);
  return { startAt: start.toISOString(), endAt: end.toISOString() };
}

function CreationFunnelInner() {
  const initial = defaultRange();
  const [slicer, setSlicer] = useState<TimeSlicerValue>({
    startAt: initial.startAt,
    endAt: initial.endAt,
  });

  const filter = { startAt: slicer.startAt, endAt: slicer.endAt };
  const query = useQuery({
    queryKey: ['dashboard', 'creation-funnel', filter],
    queryFn: () => getCreationFunnel(filter),
  });
  const data = query.data;

  const stages = data
    ? [
        { name: '作品', value: data.workCount },
        { name: '已讲述', value: data.narratedCount },
        { name: '有完成对话', value: data.dialogueDoneCount },
        { name: '已定稿', value: data.finalizedCount },
        { name: '已购买', value: data.unlockedCount },
        { name: '已打印', value: data.printedCount },
      ]
    : [];
  const funnelOption = {
    tooltip: { trigger: 'axis' as const },
    grid: { left: 90, right: 24, top: 16, bottom: 30 },
    xAxis: { type: 'value' as const, minInterval: 1 },
    yAxis: { type: 'category' as const, inverse: true, data: stages.map((s) => s.name) },
    series: [{ type: 'bar' as const, barWidth: 22, data: stages.map((s) => s.value) }],
  };

  return (
    <div data-testid="dashboard-creation-funnel">
    <Card title="L0 创作转化漏斗（零埋点）">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <TimeSlicer value={slicer} onChange={setSlicer} showPeriod={false} />
        {data?.notes && <Alert type="info" message={data.notes} showIcon />}
        <Row gutter={16}>
          <Col span={6}><Card><Statistic title="作品数" value={data?.workCount ?? 0} loading={query.isLoading} /></Card></Col>
          <Col span={6}><Card><Statistic title="定稿数" value={data?.finalizedCount ?? 0} loading={query.isLoading} /></Card></Col>
          <Col span={6}><Card><Statistic title="单次讲述均时(分)" value={data?.avgNarrationMinutes ?? 0} precision={1} loading={query.isLoading} /></Card></Col>
          <Col span={6}><Card><Statistic title="有效分享数" value={data?.shareCount ?? 0} loading={query.isLoading} /></Card></Col>
        </Row>
        <ChartContainer
          title="作品转化漏斗"
          option={funnelOption}
          loading={query.isLoading}
          empty={!data || data.workCount === 0}
          testId="creation-funnel-chart"
        />
      </Space>
    </Card>
    </div>
  );
}

export default function CreationFunnelPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.DASHBOARD_READ}
      fallback={<Result status="403" title="无权访问" subTitle="数据看板需 dashboard:read 权限。" />}
    >
      <CreationFunnelInner />
    </PermissionGate>
  );
}
